import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { addCSRFHeader } from '@/utils/csrf'
import { fetchWithRefresh } from '@/utils/fetchWithRefresh'

interface User {
  id: string
  email: string
  username: string
  subscription_tier: string
  scans_per_day?: number
  scans_used_today?: number
  scans_remaining_today?: number
  trial_days_remaining?: number
  has_access?: boolean
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (credentials: { identifier: string; password: string }) => Promise<{ success: boolean; error?: string }>
  register: (userData: { email: string; username: string; password: string; full_name?: string; recaptcha_token?: string }) => Promise<{ success: boolean; message?: string; error?: string; trial_info?: any }>
  logout: () => Promise<void>
  checkAuthStatus: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const checkAuthStatus = async () => {
    try {
      const response = await fetchWithRefresh('/api/auth/me', {
        credentials: 'include',
        headers: addCSRFHeader({
          'Content-Type': 'application/json',
        })
      })

      if (response.ok) {
        const userData = await response.json()
        setUser(userData)
      } else {
        setUser(null)
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const login = async (credentials: { identifier: string; password: string }): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetchWithRefresh('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials)
      })

      if (response.ok) {
        // After successful login, fetch complete user data including scan limits
        await checkAuthStatus()
        return { success: true }
      } else {
        const errorData = await response.json()
        console.error('Login failed:', errorData.error || 'Login failed')
        return {
          success: false,
          error: errorData.error || 'Invalid credentials'
        }
      }
    } catch (error) {
      console.error('Login error:', error)
      return {
        success: false,
        error: 'Network error. Please check your connection and try again.'
      }
    }
  }

  const register = async (userData: { email: string; username: string; password: string; full_name?: string; recaptcha_token?: string }): Promise<{ success: boolean; message?: string; error?: string; trial_info?: any }> => {
    try {
      const response = await fetchWithRefresh('/api/auth/register', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      })

      if (response.ok) {
        const result = await response.json()
        // After successful registration, fetch complete user data including scan limits
        await checkAuthStatus()
        return {
          success: true,
          message: result.message,
          trial_info: result.trial_info
        }
      } else {
        const errorData = await response.json()
        console.error('Registration failed:', errorData.error || 'Registration failed')
        return {
          success: false,
          error: errorData.error || 'Registration failed. Please try again.'
        }
      }
    } catch (error) {
      console.error('Registration error:', error)
      return {
        success: false,
        error: 'Network error. Please check your connection and try again.'
      }
    }
  }

  const logout = async () => {
    try {
      await fetchWithRefresh('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: addCSRFHeader({
          'Content-Type': 'application/json',
        })
      })
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
    }
  }

  useEffect(() => {
    checkAuthStatus()
  }, [])

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuthStatus
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}